import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cannes/chauffeur-cannes.png";
import { cityPriceFaq } from "../slugs/cityPricing";

const antibes: ChauffeurPage = {
  slug: "antibes",
  title: "VTC & private chauffeur in Antibes / Juan-les-Pins",
  subtitle:
    "Private chauffeur in Antibes: Port Vauban and its superyachts, Cap d'Antibes, Juan-les-Pins.",
  keywords: [
    "vtc antibes",
    "private chauffeur antibes",
    "juan-les-pins chauffeur",
  ],
  hero: {
    title: "VTC & private chauffeur in Antibes / Juan-les-Pins",
    subtitle:
      "Port Vauban, Cap d'Antibes and Juan-les-Pins: a chauffeur for crews and owners.",
    keywords: [
      "VTC Antibes",
      "Port Vauban chauffeur",
      "Juan-les-Pins chauffeur",
    ],
    imageSrc: heroImage.src,
    imageAlt: "VTC & private chauffeur in Antibes",
    badges: ["Port Vauban yachting", "Cap d'Antibes", "Bilingual chauffeurs"],
  },
  benefits: {
    heading: "Why a chauffeur in Antibes",
    items: [
      {
        title: "Built for yachting",
        description:
          "Port Vauban, Europe's largest marina: transfers for crews, owners and guests.",
      },
      {
        title: "Cap d'Antibes",
        description:
          "Villas and hotels on the Cap: discreet drop-off at private, secluded addresses.",
      },
      {
        title: "Juan-les-Pins",
        description:
          "Beaches, the jazz festival and nightlife: shuttles and late-night returns.",
      },
      {
        title: "Airport close by",
        description:
          "Quick link to Nice Côte d'Azur, ideal for crew rotations and guest arrivals.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services in Antibes",
    services: [
      {
        title: "Yachting transfers",
        text: "Port Vauban and the Billionaires' Quay: crews, provisioning and guests, with flexible timing.",
      },
      {
        title: "Airport transfers",
        text: "Antibes ↔ Nice airport with flight tracking, for guest arrivals and departures.",
      },
      {
        title: "Hourly hire",
        text: "A dedicated chauffeur for owners and families, from Cap d'Antibes across the Riviera.",
      },
      {
        title: "Juan-les-Pins nights",
        text: "Private beaches, the casino and festival concerts, with safe late-night returns.",
      },
      {
        title: "Old Antibes",
        text: "Provençal market, ramparts and lanes: drop-off as close as possible despite the pedestrian zone.",
      },
    ],
  },
  locations: {
    heading: "Districts & pick-up points",
    items: [
      {
        name: "Port Vauban",
        description: "Europe's largest marina, the Billionaires' Quay.",
      },
      {
        name: "Cap d'Antibes",
        description: "Private villas, Eden-Roc, La Garoupe.",
      },
      {
        name: "Juan-les-Pins",
        description: "Beaches, Pinède Gould, nightlife.",
      },
      {
        name: "Old Antibes",
        description: "Ramparts, Provençal market, Picasso museum.",
      },
      {
        name: "Marina Baie des Anges",
        description: "Residences and marina at Villeneuve-Loubet.",
      },
      {
        name: "Sophia Antipolis",
        description: "Neighbouring tech park for business travel.",
      },
    ],
  },
  generic: {
    heading: "Antibes, the Riviera's yachting capital",
    subheading:
      "Port Vauban, Cap d'Antibes and Juan-les-Pins: three rhythms, one chauffeur.",
    paragraphs: [
      "Port Vauban is Europe's largest marina, and its Billionaires' Quay hosts the biggest yachts. Crews, provisioning, guest rotations: we handle shifting schedules and precise pick-up points right at the pontoons.",
      "Cap d'Antibes lines up exceptional villas and hotels along narrow, confidential roads. A chauffeur who knows the access drops you at the gate, with no hesitation or awkward manoeuvring.",
      "On the Juan-les-Pins side, the summer season and the jazz festival concentrate private beaches, concerts and late nights. Hourly hire makes for relaxed returns, with no parking constraint and no drink-driving worry.",
    ],
    bullets: ["Flexible crew timing", "Cap villa drop-off", "Flight tracking"],
  },
  process: {
    heading: "Book a chauffeur in Antibes",
    steps: [
      "Give the pick-up point (Port Vauban pontoon, Cap villa, hotel, airport).",
      "We set the time around the yacht's, the flight's or the event's arrival.",
      "The chauffeur arrives as close as possible to the quay or private address.",
      "Direct transfer or full-day hourly hire, depending on your needs.",
    ],
  },
  faq: [
    cityPriceFaq("en", "Antibes"),
    {
      q: "Do you handle transfers from Port Vauban and the Billionaires' Quay?",
      a: "Yes. We pick up crews, owners and guests as close as possible to the pontoons, with timing matched to yacht arrivals and departures.",
    },
    {
      q: "How long between Antibes and Nice airport?",
      a: "Around 30 to 40 minutes via the A8 outside peak hours. We track guests' flights to adjust the pick-up.",
    },
    {
      q: "Can you serve the villas on Cap d'Antibes?",
      a: "Yes, we drop off and pick up directly at the villa or hotel address, discreetly and in coordination with the concierge if needed.",
    },
    {
      q: "Do you handle late returns during the Juan-les-Pins festival?",
      a: "Yes, through hourly hire: the chauffeur stays available for a late-night return after a concert or an evening at a private beach.",
    },
  ],
  relatedLinks: [
    {
      label: "Private transfer Cannes ⇄ Nice Airport",
      href: "/en/chauffeur/transfers/cannes-nice-airport",
    },
    {
      label: "Private transfer Nice Airport ⇄ Saint-Jean-Cap-Ferrat",
      href: "/en/chauffeur/transfers/nice-airport-saint-jean-cap-ferrat",
    },
    { label: "Our chauffeur transfers", href: "/en/chauffeur/transfers" },
  ],
  cta: {
    title: "Your private chauffeur in Antibes",
    text: "Yachting at Port Vauban, a Cap villa or a Juan-les-Pins night out: book your chauffeur.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title: "Car service Antibes — private chauffeur, Port Vauban",
    description:
      "Private car service in Antibes / Juan-les-Pins: Port Vauban, superyachts, Cap d'Antibes and Nice airport. Crews, owners, 24/7. Book online.",
    canonical: "/en/chauffeur/antibes",
  },
};

export default antibes;
